import {
  PortedTicketData as TicketData,
  PortedCartItem as CartItem,
  PortedTaxResult as TaxResult,
  PortedCustomizationOption as CustomizationOption,
} from './types';
import { groupItemsByCategory, aggregateCustomizationOptions } from './grouping';
import { calculateTax, getTaxDisplayFlags } from './tax';
import { tk, tkOrderTypeUpper, tkPayment } from './i18nShim';
import { catStart, catEnd } from './categoryMarker';
import { formatDateTime, currentLocale } from './dateFormat';

/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * PLAN DE TICKET PARTAGÉ — socle des QUATRE MODULES EXPÉRIMENTAUX
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Ce fichier ne produit AUCUN texte imprimable : il produit un PLAN, c'est-à-dire la
 * liste des lignes du ticket sous forme typée (titre, ligne à deux colonnes, séparateur…).
 * Ce sont ensuite les modules de test qui décident COMMENT ce plan devient du papier :
 *
 *   • TEST 1 `ticketFormatterRaster.ts`       — tout le plan devient une IMAGE `<img>` ;
 *   • TEST 2 `ticketFormatterRasterHybrid.ts` — seul le bloc articles devient une image ;
 *   • TEST 3 `ticketFormatterAntiPattern.ts`  — texte, colonnes natives, quantité `2× Nom` ;
 *   • TEST 4 `ticketFormatterNoAnchor.ts`     — texte, colonnes natives, quantité `Nom x2`.
 *
 * ⚠️ C'EST LE POINT CENTRAL DE LA MANIP : les quatre modules impriment EXACTEMENT le même
 * ticket (mêmes lignes, mêmes montants, même ordre). Seule la TECHNOLOGIE d'émission
 * change, donc toute différence sur le papier est imputable à la technologie et à rien
 * d'autre. C'est ce qui rend la comparaison exploitable.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * CE QUE LES MODULES CHERCHENT À DÉPARTAGER (vérifié dans MainActivity.java v5.8)
 * ───────────────────────────────────────────────────────────────────────────────
 * `getAsyncEscPosPrinter()` (MainActivity:4792) pré-traite CHAQUE ligne avant DantSu :
 *
 *     if (line.trim().equalsIgnoreCase("ARTICLES")) { inArticlesSection = true; … }
 *     …
 *     else if (inArticlesSection && cleanedLine.matches(".*x\\d+.*")) {
 *         String[] parts = cleanedLine.split("x\\d+");
 *         …  formattedText.append("[L]").append(itemName).append(" x").append(quantity);
 *     }
 *
 * `cleanedLine` = la ligne DÉBARRASSÉE de `[L]`/`[C]`/`[R]`/`<b>`/`<u>`/`<font…>` puis
 * trimée. Donc `[L]Pizza x2[R]18,00` devient `Pizza x218,00`, la regex matche, la ligne
 * est réécrite en `[L]Pizza x21800` : **LA COLONNE PRIX EST DÉTRUITE ET LA QUANTITÉ
 * CORROMPUE**. Deux échappatoires, et une seule expérience peut les distinguer :
 *   1. ne jamais armer `inArticlesSection` (aucune ligne valant exactement `ARTICLES`) ;
 *   2. ne jamais écrire `x<chiffres>` (le `x` doit être ASCII et suivi d'un chiffre).
 * TEST 4 valide (1) — il arme volontairement le motif mais pas l'ancre.
 * TEST 3 valide (2) — il arme volontairement l'ancre mais pas le motif.
 * TEST 1 et 2 s'affranchissent des deux : une image n'est pas du texte, aucune règle de
 * réécriture ne peut l'atteindre.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * RÈGLES DU PONT RESPECTÉES ICI (elles ne dépendent PAS du verrou `ARTICLES`)
 * ───────────────────────────────────────────────────────────────────────────────
 * • `\n` seul, jamais `\r` (les pilotes font `text.split("\n")`).
 * • Aucun octet ESC/POS brut : les profils de test posent `useEscPos: false`.
 * • Aucun caractère hors CP1252 (`getBytes("windows-1252")` sinon → `?`).
 * • Jamais 24 tirets consécutifs : le natif dédoublonne alors deux lignes identiques
 *   consécutives (un article commandé 2× de suite DISPARAÎT) — on sépare au `=`.
 * • Aucune ligne commençant par `customer :` / `notes:` / `note:` : le natif injecte
 *   sinon une ligne de 24 tirets, ce qui arme le dédoublonnage ci-dessus.
 * • Aucune coupe ni avance finale : le natif coupe déjà (`printFormattedTextAndCut`).
 */

/* ═══════════════════════════════════════════════════════════════════════════════
 * MODÈLE DE LIGNE
 * ═══════════════════════════════════════════════════════════════════════════════ */

export type PlanLine =
  /** Ligne vide. */
  | { k: 'blank' }
  /** Séparateur pleine largeur (rendu en `=`, jamais en `-` : voir R6 ci-dessus). */
  | { k: 'sep' }
  /**
   * En-tête de la section articles. C'EST LA LIGNE SENSIBLE : selon le module elle est
   * émise telle quelle (`ARTICLES`, ce qui ARME le pré-traitement natif) ou décorée
   * (`- - ARTICLES - -`, ce qui le laisse inerte).
   */
  | { k: 'anchor'; t: string; count?: number }
  /**
   * Ligne centrée. `big` = titre agrandi (nom d'établissement) : ~2× le corps, SANS inversion.
   * `emphasis` = info clé à faire ressortir (n° de commande, type, table) : les modules IMAGE
   * la rendent ~2× le corps ET en VIDÉO INVERSÉE (blanc sur bloc noir) quand l'exploitant l'a
   * activé (`config.rasterHighlightKeyInfo !== false`). Les modules TEXTE ignorent ces drapeaux.
   */
  | { k: 'center'; t: string; b?: boolean; big?: boolean; emphasis?: boolean }
  | { k: 'left'; t: string; b?: boolean }
  /** Libellé à gauche, montant à droite. */
  | { k: 'pair'; l: string; r: string; b?: boolean }
  /** Balise de routage cuisine `[CAT:…]` — TOUJOURS émise en texte brut, jamais en image. */
  | { k: 'marker'; t: string };

/** Forme d'écriture de la quantité — c'est la variable de l'expérience TEST 3 / TEST 4. */
export type QuantityStyle =
  /** `Pizza x2` — déclenche `.*x\d+.*` si l'ancre `ARTICLES` est armée. */
  | 'x-suffix'
  /** `2× Pizza` — `×` = U+00D7, présent en CP1252, JAMAIS reconnu par la regex Java. */
  | 'multiply-prefix';

export interface PlanOptions {
  /** Largeur de composition en caractères (48 en 80 mm, 32 en 58 mm). */
  width: number;
  quantityStyle: QuantityStyle;
  /** Émettre les balises `[CAT:…]` de routage cuisine (défaut : oui). */
  emitCategoryMarkers?: boolean;
  /** Suffixe monétaire (`EUR` par défaut : le glyphe `€` dépend de la page de code). */
  currency?: string;
  /** Retirer les accents (clones chinois sans page de code 16). */
  asciiOnly?: boolean;
}

export interface TicketPlan {
  lines: PlanLine[];
  /** Index de la première ligne du bloc ARTICLES (l'ancre elle-même). */
  itemsFrom: number;
  /** Index de fin (exclu) du bloc ARTICLES — utilisé par le module HYBRIDE. */
  itemsTo: number;
  /** Largeur de composition retenue. */
  width: number;
}

/** Signe multiplication CP1252 (0xD7) — le seul « x » que la regex Java ne voit pas. */
export const MULTIPLY_SIGN = '×';

/* ═══════════════════════════════════════════════════════════════════════════════
 * ASSAINISSEMENT CP1252
 * ═══════════════════════════════════════════════════════════════════════════════
 * Même logique que le module unifié : `getBytes("windows-1252")` côté natif transforme
 * tout le reste en `?` ET fausse le remplissage (un emoji = 2 unités UTF-16 en JS pour
 * 1 octet imprimé, donc 2 colonnes comptées pour 1 imprimée).
 */
const CHAR_FIXES: Array<[RegExp, string]> = [
  [/[  -​  　﻿]/g, ' '],
  [/[‐-―−]/g, '-'],
  [/[‘’‚′´]/g, "'"],
  [/[“”„″«»]/g, '"'],
  [/[…]/g, '...'],
  [/[•·●▪■]/g, '*'],
  [/[→⇒›]/g, '>'],
  [/[←⇐‹]/g, '<'],
  [/[✓✔]/g, 'v'],
  [/[€]/g, 'EUR'],
  [/[œ]/g, 'oe'],
  [/[Œ]/g, 'OE'],
  [/[™]/g, '(TM)'],
  [/[©]/g, '(C)'],
  [/[®]/g, '(R)'],
];

const COMBINING = /[̀-ͯ]/g;
/** ASCII imprimable + Latin-1 haut (accents français, `°`, `§`, `×`…). */
const CP1252_SAFE = /^[\x20-\x7E¡-ÿ]$/;

/**
 * Rend une chaîne SÛRE pour l'encodage Windows-1252 du pont, et neutralise les
 * caractères qui feraient croire à une balise (`[`, `]`, `<`, `>`) — une balise inconnue
 * s'imprime en clair, et un `[C]` au milieu d'un nom de produit changerait l'alignement.
 */
export const sanitizeCp1252 = (input: unknown, asciiOnly = false): string => {
  let s = String(input ?? '').normalize('NFC');
  for (const [re, rep] of CHAR_FIXES) s = s.replace(re, rep);
  s = s.replace(/[\r\n\t\v\f]/g, ' ');
  s = s.replace(/[\x00-\x1F\x7F]/g, '');

  let out = '';
  for (const ch of s) {
    if (CP1252_SAFE.test(ch)) {
      out += ch;
      continue;
    }
    const folded = ch.normalize('NFD').replace(COMBINING, '');
    out += folded.length === 1 && CP1252_SAFE.test(folded) ? folded : '';
  }

  if (asciiOnly) {
    out = out.normalize('NFD').replace(COMBINING, '');
    out = out.replace(/[^\x20-\x7E]/g, '');
  }

  // Jamais 24 tirets d'affilée (dédoublonnage natif), ni de faux chevrons/crochets.
  out = out.replace(/-{4,}/g, '---');
  out = out.replace(/[<>[\]]/g, ' ');

  return out.replace(/\s+/g, ' ').trim();
};

/* ═══════════════════════════════════════════════════════════════════════════════
 * MISE EN FORME
 * ═══════════════════════════════════════════════════════════════════════════════ */

const decimalSep = (): string => {
  try {
    return (1.1).toLocaleString(currentLocale()).includes(',') ? ',' : '.';
  } catch {
    return ',';
  }
};

/** Montant SANS groupement de milliers : longueur prévisible, aucune espace insécable. */
export const money = (v: unknown): string => {
  const n = Number(v);
  return (Number.isFinite(n) ? n : 0).toFixed(2).replace('.', decimalSep());
};

const round2 = (v: unknown): number => Math.round((Number(v) || 0) * 100) / 100;

/**
 * Coercition ROBUSTE d'un montant vers un nombre.
 *
 * ⚠️ RÉGRESSION CORRIGÉE — « 0,00 » sur les ARTICLES du ticket de routage cuisine.
 * `unitPriceOf`/le prix des options faisaient `Number(x) || 0`. Or l'API — et surtout les
 * saisies FR de la caisse (poids, prix au kilo, code-barres…) — livrent parfois le prix sous
 * forme de CHAÎNE À VIRGULE (« 9,50 ») ou avec séparateur de milliers en espace (« 1 234,50 »).
 * `Number("9,50")` vaut `NaN`, donc `Number("9,50") || 0` retombait à 0 → la ligne article
 * sortait « 0,00 » (le rendu RASTER de la caisse comme le rendu TEXTE routé cuisine partagent
 * CE calcul via `buildTicketPlan`). On normalise donc la virgule décimale et on retire les
 * espaces (y compris insécables ` `/` `) AVANT conversion. Un `number` fini est
 * renvoyé tel quel ; `null`/`undefined`/vide/illisible → 0 (un article réellement gratuit
 * reste bien à 0).
 */
export const toAmount = (v: unknown): number => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  if (typeof v === 'string') {
    const cleaned = v
      .replace(/[\s  ]/g, '') // milliers en espace (dont insécables)
      .replace(',', '.') // virgule décimale FR → point
      .replace(/[^0-9.+-]/g, ''); // devise/parasites (« € », « EUR »…)
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
};

/** Préfixes qui déclenchent l'injection d'un séparateur de 24 tirets côté natif. */
const COMMENT_PREFIXES = ['customer :', 'notes:', 'note:'];

/**
 * Empêche une ligne de passer pour un « commentaire ». On préfixe d'une ÉTOILE et non
 * d'un espace : le natif fait `trim()` avant le test, un espace ne protégerait de rien.
 */
export const guardComment = (text: string): string => {
  const probe = text.trim().toLowerCase();
  return COMMENT_PREFIXES.some((p) => probe.startsWith(p)) ? `* ${text}` : text;
};

/** Ligne à deux colonnes remplie EN JS, longueur toujours exactement `w`. */
export const padPair = (left: string, right: string, w: number): string => {
  const r = right.slice(0, Math.max(0, w));
  const maxLeft = Math.max(0, w - r.length - (r.length ? 1 : 0));
  const l = left.length > maxLeft ? left.slice(0, maxLeft) : left;
  const gap = Math.max(r.length ? 1 : 0, w - l.length - r.length);
  return (l + ' '.repeat(gap) + r).slice(0, w);
};

/**
 * Tuile un MOTIF de séparateur choisi par l'exploitant (`config.separator`) sur `width`
 * caractères, exactement comme les formateurs Modern/Legacy (`ensureSeparatorWidth`) : un
 * motif court (`*`, `- `, `=`) est répété jusqu'à remplir la ligne, un motif long est tronqué.
 * Motif vide (« aucun séparateur ») → chaîne vide.
 */
export const tileSeparator = (pattern: string, width: number): string => {
  const raw = String(pattern ?? '');
  if (raw.trim() === '') return '';
  // Motif ≤ 3 caractères : conservé tel quel (garde un éventuel espace de « - ») ; sinon trimé.
  const unit = raw.length <= 3 ? raw : raw.trim();
  if (unit.length === 0 || width <= 0) return '';
  let out = '';
  while (out.length < width) out += unit;
  return out.slice(0, width);
};

/** Centrage calculé en JS (utilisé pour le rendu IMAGE, qui n'a pas de balise `[C]`). */
export const padCenter = (text: string, w: number): string => {
  const t = text.slice(0, w);
  const leftPad = Math.max(0, Math.floor((w - t.length) / 2));
  return ' '.repeat(leftPad) + t;
};

/** Retour à la ligne LOGICIEL : aucun pilote du parc n'a de word-wrap. */
export const wrapText = (text: string, maxLen: number): string[] => {
  const limit = Math.max(4, maxLen);
  const words = text.split(' ').filter(Boolean);
  const out: string[] = [];
  let cur = '';
  for (const word of words) {
    const piece = word.length > limit ? word.slice(0, limit) : word;
    if (!cur) cur = piece;
    else if (cur.length + 1 + piece.length <= limit) cur += ` ${piece}`;
    else {
      out.push(cur);
      cur = piece;
    }
  }
  if (cur) out.push(cur);
  return out.length ? out : [''];
};

/* ═══════════════════════════════════════════════════════════════════════════════
 * CONSTRUCTION DU PLAN
 * ═══════════════════════════════════════════════════════════════════════════════ */

export function buildTicketPlan(data: TicketData, opts: PlanOptions): TicketPlan {
  const w = Math.max(24, Math.min(64, Math.floor(opts.width || 48)));
  const ascii = opts.asciiOnly === true;
  const S = (v: unknown) => sanitizeCp1252(v, ascii);
  const cur = S(opts.currency ?? 'EUR') || 'EUR';
  const withMarkers = opts.emitCategoryMarkers !== false;

  /**
   * Budget du LIBELLÉ, identique pour les quatre modules.
   *
   * ⚠️ Ce n'est pas un choix esthétique : TEST 3 et TEST 4 utilisent les COLONNES NATIVES
   * `[L]…[R]`, et DantSu découpe alors la ligne en `nbrCharactersPerLine / nbrColonnes`
   * (`PrinterTextParserLine:39`), soit 24 caractères par colonne en 48. Un libellé plus
   * long déborderait sur la colonne du prix. On plafonne donc le libellé à la moitié de
   * la largeur POUR TOUS LES MODULES, sinon les tickets ne seraient plus comparables.
   */
  const labelBudget = Math.floor(w / 2) - 1;

  const L: PlanLine[] = [];
  const blank = () => L.push({ k: 'blank' });
  const sep = () => L.push({ k: 'sep' });
  const cen = (t: string, o?: { b?: boolean; big?: boolean; emphasis?: boolean }) =>
    t && L.push({ k: 'center', t, b: o?.b, big: o?.big, emphasis: o?.emphasis });
  const left = (t: string, b?: boolean) => t && L.push({ k: 'left', t, b });
  const pair = (l: string, r: string, b?: boolean) => L.push({ k: 'pair', l, r, b });

  /** Libellé + montant, replié sur plusieurs lignes quand le libellé dépasse le budget. */
  const priced = (label: string, amount: string, bold?: boolean) => {
    const budget = Math.min(labelBudget, w - amount.length - 1);
    if (label.length <= budget) {
      pair(label, amount, bold);
      return;
    }
    const parts = wrapText(label, budget);
    for (let i = 0; i < parts.length - 1; i += 1) left(parts[i], bold);
    pair(parts[parts.length - 1], amount, bold);
  };

  /* ── 1. En-tête ÉTABLISSEMENT ─────────────────────────────────────────────── */
  // Bandeau de SOURCE en tête (ex. « UBER EATS ») — gros, gras, mis en évidence, AVANT le
  // nom du resto. `emphasis` → vidéo inversée sur les modules image (comme les infos clés).
  if (data.topBanner) cen(S(data.topBanner).toUpperCase(), { b: true, big: true, emphasis: true });
  const storeName = S(data.storeInfo?.name).toUpperCase();
  cen(storeName || S(tk('header.defaultRestaurant')).toUpperCase(), { b: true, big: true });
  cen(S(data.storeInfo?.address));
  if (data.storeInfo?.phone) cen(`${S(tk('header.phoneShort'))} ${S(data.storeInfo.phone)}`);
  sep();

  /* ── 2. En-tête TRANSACTION ───────────────────────────────────────────────── */
  const rawCode = S(data.orderCode ?? '');
  const shortCode = rawCode && rawCode !== 'N/A' ? rawCode.slice(-2).padStart(2, '0') : rawCode;
  const code = (data.printFullOrderCode ? rawCode : shortCode) || '--';
  // INFO CLÉ n°1 : le numéro de commande. Marquée `emphasis` À LA SOURCE (pas de détection
  // par regex sur le texte) → les modules image la rendent 2× + vidéo inversée.
  cen(`${S(tk('header.orderUpper'))} #${code}`, { b: true, emphasis: true });

  // INFO CLÉ n°2 et n°3 : le TYPE de commande et le n° de TABLE, réunis sur une seule ligne
  // centrée (« SUR PLACE - TABLE 7 »). Même marquage `emphasis` → 2× + vidéo inversée.
  const typeLine = [
    S(tkOrderTypeUpper(data.orderType)),
    data.tableNumber ? `${S(tk('header.tableUpper'))} ${S(data.tableNumber)}` : '',
  ]
    .filter(Boolean)
    .join(' - ');
  cen(typeLine, { b: true, emphasis: true });

  cen(S(formatDateTime(data.orderDate)));

  const seller = [data.seller_code, data.created_by_name]
    .filter((v) => !!v && String(v).trim() !== '')
    .map((v) => S(v))
    .join(' - ');
  if (seller) cen(`${S(tk('header.seller'))} ${seller}`);

  if (data.customerName) cen(`${S(tk('header.client'))} : ${S(data.customerName)}`);
  if (data.customerPhone) cen(`${S(tk('header.phoneShort'))} ${S(data.customerPhone)}`);
  if (data.customerAddress) {
    for (const part of wrapText(`${S(tk('header.address'))} : ${S(data.customerAddress)}`, w)) {
      left(part);
    }
  }
  if (data.confirmationCode !== undefined && data.confirmationCode !== null) {
    cen(`${S(tk('header.clientNumber'))} ${S(data.confirmationCode)}`);
  }
  sep();

  /* ── 3. ARTICLES ──────────────────────────────────────────────────────────── */
  const items = Array.isArray(data.items) ? data.items : [];
  // Nombre total d'articles (somme des quantités) — affiché ENTRE PARENTHÈSES après le mot
  // « ARTICLES » (gain de hauteur vs une ligne dédiée). Porté par la ligne d'ancre ; ajouté
  // seulement au rendu DÉCORÉ/image (jamais au mode « bare » = ancre-piège du profil TEST 3).
  const totalArticles = items.reduce((sum, it) => sum + (Number(it.quantity) || 1), 0);
  const itemsFrom = L.length;
  L.push({
    k: 'anchor',
    t: S(tk('items.sectionTitle')).toUpperCase() || 'ARTICLES',
    count: totalArticles > 0 ? totalArticles : undefined,
  });
  blank();
  const groups = groupItemsByCategory(items, data.categories || []);

  // Le prix des options est facturé sur SA PROPRE ligne : on ne le réinjecte pas dans le
  // prix de l'article, sinon la somme des lignes diverge d'`invoiceTotal`.
  // ⚠️ `toAmount` (et non `Number(...) || 0`) : un prix en chaîne à virgule (« 9,50 ») sortait
  // sinon « 0,00 » sur le ticket cuisine (voir l'en-tête de `toAmount`).
  const unitPriceOf = (item: CartItem): number => toAmount(item.menuItem?.price);

  const pushOptions = (item: CartItem) => {
    const buckets = item.customizations || {};
    const flat: CustomizationOption[] = [];
    for (const key of Object.keys(buckets)) {
      const arr = buckets[key];
      if (Array.isArray(arr)) flat.push(...arr);
    }
    if (!flat.length) return;
    for (const opt of aggregateCustomizationOptions(flat)) {
      const qty = opt.quantity > 1 ? `${opt.quantity} ` : '';
      const label = `  + ${qty}${S(opt.name)}`;
      const price = toAmount(opt.price);
      if (price > 0) priced(label, money(price * opt.quantity * (Number(item.quantity) || 1)));
      else left(label);
    }
  };

  const pushNote = (item: CartItem) => {
    if (!item.note || !String(item.note).trim()) return;
    // « Note : » avec espace AVANT le deux-points : `note:` déclencherait l'injection
    // d'une ligne de 24 tirets côté natif.
    const text = `  ${S(tk('items.note'))} : ${S(item.note)}`;
    for (const part of wrapText(text, w)) left(guardComment(part));
  };

  let printedAny = false;
  groups.forEach((group, index) => {
    if (!group.items.length) return;
    printedAny = true;

    cen(S(group.categoryName).toUpperCase(), { b: true });

    if (withMarkers) L.push({ k: 'marker', t: catStart(group.categoryId, group.categoryName, index) });

    for (const item of group.items) {
      const qty = Number(item.quantity) || 1;
      const name = S(item.menuItem?.name);
      // ⚠️ LA VARIABLE DE L'EXPÉRIENCE : `Nom x2` (motif armé) vs `2× Nom` (motif absent).
      const label =
        opts.quantityStyle === 'x-suffix'
          ? `${name} x${qty}`
          : `${qty}${MULTIPLY_SIGN} ${name}`;
      priced(label, money(unitPriceOf(item) * qty));
      pushOptions(item);
      pushNote(item);
    }

    if (withMarkers) L.push({ k: 'marker', t: catEnd(group.categoryId, group.categoryName, index) });
    blank();
  });

  if (!printedAny) cen(S(tk('items.none')));
  const itemsTo = L.length;

  /* ── 4. TOTAUX / TVA ──────────────────────────────────────────────────────── */
  // On ne recalcule JAMAIS le HT à partir du TTC quand l'API le fournit, et on ne
  // re-somme jamais les lignes : un écart d'un centime rend le ticket non conforme NF525.
  const tr: TaxResult | undefined =
    data.taxResult && !Array.isArray(data.taxResult) ? (data.taxResult as TaxResult) : undefined;
  const rateLines = Array.isArray(data.taxResult)
    ? (data.taxResult as Array<{ rate: number; amount: number }>)
    : [];
  const totals = data.orderTotals;

  const totalTTC = round2(tr?.totalTTC ?? totals?.invoiceTotal ?? data.total ?? 0);
  const loyaltyDiscount = round2(totals?.loyaltyDiscount || 0);
  const loyaltyPoints = Number(totals?.loyaltyPointsUsed) || 0;
  const baseTTC = loyaltyDiscount > 0 ? round2(totalTTC + loyaltyDiscount) : totalTTC;

  const apiRate = tr?.taxRate ?? null;
  const hasHT = tr?.subtotalHT !== undefined && tr?.subtotalHT !== null;
  const hasTax = tr?.taxAmount !== undefined && tr?.taxAmount !== null;
  const fb = hasHT && hasTax && apiRate !== null ? null : calculateTax(baseTTC, apiRate);
  const taxRate = apiRate !== null ? apiRate : fb ? fb.taxRate : 0;
  const subtotalHT = hasHT ? round2(tr?.subtotalHT) : round2(fb?.subtotalHT ?? totals?.subtotal ?? 0);
  const taxAmount = hasTax ? round2(tr?.taxAmount) : round2(fb?.taxAmount ?? totals?.taxAmount ?? 0);
  const rateFmt = taxRate % 1 === 0 ? taxRate.toFixed(0) : taxRate.toFixed(1);

  const rawMode = data.taxSettings?.displayMode;
  const flags = getTaxDisplayFlags(rawMode === 'hidden' ? 'compact' : rawMode || 'detailed');

  sep();
  if (flags.showSubtotalHT) priced(S(tk('totals.subtotalHT')), money(subtotalHT));
  if (flags.showTaxAmount) {
    if (rateLines.length) {
      for (const r of rateLines) {
        const rf = Number(r.rate) % 1 === 0 ? Number(r.rate).toFixed(0) : Number(r.rate).toFixed(1);
        priced(S(tk('totals.taxWithRate', { rate: rf })), money(r.amount));
      }
    } else {
      priced(
        flags.showTaxRate ? S(tk('totals.taxWithRate', { rate: rateFmt })) : S(tk('totals.tax')),
        money(taxAmount)
      );
    }
  }

  const deliveryFee = round2(data.deliveryFee || 0);
  if (deliveryFee > 0) priced(S(tk('totals.deliveryFeeShort')), money(deliveryFee));

  const discount = round2(data.discount || 0);
  if (discount > 0) priced(S(tk('totals.discount')), `-${money(discount)}`);

  // Remises NOMMÉES de la borne (HAPPY HOUR).
  // ⚠️ NF525 : un prix qui diffère du catalogue doit être justifié SUR le ticket. La
  // borne fournit le montant ET le libellé officiels du serveur — on n'en recalcule
  // aucun, on les imprime tels quels.
  for (const line of Array.isArray(data.extraDiscountLines) ? data.extraDiscountLines : []) {
    const amount = round2(line?.amount);
    if (amount > 0) priced(S(line.label), `-${money(amount)}`);
  }

  if (loyaltyDiscount > 0) {
    const label = loyaltyPoints
      ? S(tk('totals.loyaltyDiscountPoints', { points: loyaltyPoints }))
      : S(tk('totals.loyaltyDiscount'));
    priced(label.replace(/:$/, ''), `-${money(loyaltyDiscount)}`);
  }

  priced(S(tk('totals.totalTTCUpper')), `${money(totalTTC)} ${cur}`, true);
  sep();

  /* ── 5. PAIEMENT ──────────────────────────────────────────────────────────── */
  const payLines = Array.isArray(data.paymentLines) ? data.paymentLines : [];
  if (data.isPaymentPending) {
    cen(S(tk('payment.toCollect')), { b: true });
  } else if (payLines.length > 1) {
    left(`${S(tk('payment.label'))} : ${S(tk('payment.mixed'))}`, true);
    for (const p of payLines) priced(`  ${S(p.label)}`, money(p.amount));
  } else if (payLines.length === 1) {
    priced(S(payLines[0].label), money(payLines[0].amount));
  } else {
    const label =
      S(data.paymentModeLabel) ||
      (data.paymentMethod ? S(tkPayment(data.paymentMethod)) : S(tk('payment.notSpecified')));
    left(`${S(tk('payment.label'))} : ${label}`);
  }

  /* ── 6. NOTES de commande ─────────────────────────────────────────────────── */
  const notes: Array<[string, string]> = [];
  if (data.customerNote) notes.push([S(tk('items.customerNoteUpper')), S(data.customerNote)]);
  if (data.delivery_note) notes.push([S(tk('notes.delivery')), S(data.delivery_note)]);
  if (data.pickup_note) notes.push([S(tk('notes.pickup')), S(data.pickup_note)]);
  if (notes.length) {
    blank();
    for (const [label, text] of notes) {
      for (const part of wrapText(`${label} : ${text}`, w)) left(guardComment(part));
    }
  }

  /* ── 7. PIED ──────────────────────────────────────────────────────────────── */
  blank();
  cen(S(tk('footer.thankYou')));
  if (data.profileSettings?.siretMessage) cen(S(data.profileSettings.siretMessage));
  if (data.profileSettings?.tvaMessage) cen(S(data.profileSettings.tvaMessage));
  // Aucune coupe, aucune avance : le natif le fait déjà, inconditionnellement.

  return { lines: L, itemsFrom, itemsTo, width: w };
}

/* ═══════════════════════════════════════════════════════════════════════════════
 * RENDU 1 — TEXTE À CHASSE FIXE (utilisé par le module IMAGE)
 * ═══════════════════════════════════════════════════════════════════════════════
 * Le rendu image est une PHOTO de ce texte : tout le remplissage est calculé ici, en JS,
 * exactement comme pour les modules texte. C'est ce qui garantit que les quatre modules
 * montrent la MÊME mise en page — seule la technologie d'émission change.
 */
export interface MonoLine {
  text: string;
  bold?: boolean;
  /** Multiplicateur de taille (1 = normal, 2 = titre). */
  scale?: number;
  /**
   * Centrage à faire EN PIXELS par le rasteriseur, et non en espaces.
   *
   * ⚠️ Indispensable pour les lignes agrandies : une ligne remplie à `w` caractères puis
   * dessinée en double taille occupe `2 × w` cellules et DÉBORDE de l'image (la fin est
   * rognée et le texte part vers la droite). Repéré à l'œil sur le rendu ASCII de la
   * trame — pas en relisant le code.
   */
  center?: boolean;
}

/**
 * @param sepText  Séparateur DÉJÀ tuilé à `w` (via `tileSeparator`) choisi par l'exploitant.
 *                 `undefined` → séparateur historique `=` pleine largeur. Dans l'IMAGE on peut
 *                 honorer n'importe quel caractère : c'est du pixel, pas du texte soumis au pont.
 */
export const planLineToMono = (ln: PlanLine, w: number, sepText?: string): MonoLine | null => {
  switch (ln.k) {
    case 'blank':
      return { text: '' };
    case 'sep':
      return { text: sepText !== undefined ? sepText : '='.repeat(w) };
    case 'anchor':
      // Centrage EN PIXELS (drapeau `center`), comme toutes les autres lignes centrées de
      // l'image. Le remplissage `padCenter` centrait sur une grille de `w` (48) caractères
      // alors que la police est calculée pour ~39 (FONT_FILL_RATIO) → le mot « ARTICLES »
      // ressortait DÉCALÉ vers la droite. Le centrage pixel est exact et aligné avec le reste.
      return { text: ln.t.trim() + (ln.count ? ` (${ln.count})` : ''), bold: true, center: true };
    case 'center':
      // Toutes les lignes centrées passent par le centrage EN PIXELS (exact), agrandies ou non :
      // « ARTICLES », les noms de catégorie et les détails client sont ainsi tous alignés.
      return { text: ln.t.trim(), bold: ln.b, center: true };
    case 'left':
      return { text: ln.t.slice(0, w), bold: ln.b };
    case 'pair':
      return { text: padPair(ln.l, ln.r, w), bold: ln.b };
    case 'marker':
      // Les balises de routage ne sont JAMAIS mises en image : `printQueue` doit pouvoir
      // les lire dans le texte pour ventiler vers les imprimantes cuisine.
      return null;
  }
  return null;
};

/* ═══════════════════════════════════════════════════════════════════════════════
 * RENDU 2 — COLONNES NATIVES `[L]…[R]` (modules TEXTE, TEST 3 et TEST 4)
 * ═══════════════════════════════════════════════════════════════════════════════
 * Ici on ne calcule AUCUN remplissage : on laisse DantSu répartir les colonnes
 * (`PrinterTextParserLine` → `nbrCharColumn = largeur / nbrColonnes`, puis
 * `PrinterTextParserString` aligne chaque colonne). C'est précisément ce que le module
 * unifié s'interdit — et c'est ce qu'on veut mesurer ici.
 */

export interface NativeRenderOptions {
  /**
   * `'bare'`   → l'en-tête de section est émis EXACTEMENT `ARTICLES`, ce qui ARME le
   *              pré-traitement natif (TEST 3 : on veut prouver que sans le motif
   *              `x<chiffres>`, l'ancre armée ne détruit plus rien).
   * `'decorated'` → l'en-tête est décoré, l'ancre reste INERTE (TEST 4).
   */
  anchorMode: 'bare' | 'decorated';
  /**
   * Remplace tout `x` suivi d'un chiffre par `×` (U+00D7) et espace `taille:`.
   * Ceinture ET bretelles du TEST 3 : un nom de produit du type « Box 2x50cl »
   * réarmerait sinon la réécriture, et fausserait la conclusion.
   */
  killXPattern?: boolean;
  /** Émettre les balises de routage `[CAT:…]` (défaut : oui). */
  emitMarkers?: boolean;
  /**
   * Séparateur choisi par l'exploitant, DÉJÀ tuilé à `w` et rendu SÛR pour le pont
   * (assaini CP1252, sans run de ≥24 tirets qui armerait le dédoublonnage natif) — voir
   * `textSafeSeparator` côté raster-hybride. `undefined` → séparateur historique `=`.
   * Chaîne vide → aucune ligne de séparation (choix « Aucun » de l'exploitant).
   */
  separator?: string;
  /**
   * Bandeaux noirs NATIFS pour les infos clés (n° de commande, type/table, marquées
   * `emphasis` à la source). Les lignes concernées sortent en `<font size='tall'
   * color='bg-black'>` → VIDÉO INVERSÉE ESC/POS (`GS B 1`, standard Epson) : même « bandeau
   * noir » que le ticket IMAGE, mais en TEXTE → aucune image, donc AUCUNE saturation du
   * tampon en USB (cf. le pont USB sans temporisation). `tall` = double hauteur, largeur
   * inchangée → pas de débordement de colonne sur les lignes longues.
   * ⚠️ Interprété uniquement sur le chemin ESC/POS du pont (Epson/génériques). Sur Sunmi la
   * balise s'imprime en clair, sur Star USB elle est supprimée : réserver aux profils USB/BT
   * Epson-génériques.
   */
  bannerEmphasis?: boolean;
}

const boldTag = (t: string, on?: boolean): string => (on ? `<b>${t}</b>` : t);

/**
 * Neutralise ce qui déclencherait une réécriture native quand l'ancre est armée.
 * ⚠️ La regex Java est `.*x\d+.*` : `x` MINUSCULE et ASCII uniquement. `X` majuscule et
 * `×` (U+00D7) ne matchent pas — d'où le remplacement ciblé.
 */
const killXDigit = (t: string): string =>
  t.replace(/x(?=\d)/g, MULTIPLY_SIGN).replace(/taille:/gi, 'taille :');

export const renderNativeColumns = (
  lines: PlanLine[],
  w: number,
  opts: NativeRenderOptions
): string[] => {
  const out: string[] = [];
  /** Repère la ligne d'ancre VOULUE, seule autorisée à valoir exactement `ARTICLES`. */
  const isAnchor: boolean[] = [];
  const emitMarkers = opts.emitMarkers !== false;

  const push = (text: string, anchor = false) => {
    out.push(text);
    isAnchor.push(anchor);
  };

  for (const ln of lines) {
    switch (ln.k) {
      case 'blank':
        push('');
        break;
      case 'sep': {
        // Séparateur exploitant si fourni (déjà sûr), sinon `=` pleine largeur (historique).
        const sepText = opts.separator !== undefined ? opts.separator : '='.repeat(w);
        push(sepText === '' ? '' : `[C]${sepText}`);
        break;
      }
      case 'anchor':
        // TEST 3 : ligne NUE, sans `[C]` — le natif teste `line.trim()`, donc la moindre
        // balise suffirait à désarmer le piège et l'expérience ne prouverait rien.
        push(
          opts.anchorMode === 'bare'
            ? ln.t
            : `[C]- - ${ln.t}${ln.count ? ` (${ln.count})` : ''} - -`,
          true
        );
        break;
      case 'center':
        if (opts.bannerEmphasis && ln.emphasis && ln.t.trim() !== '') {
          // Info clé → BANDEAU NOIR NATIF (vidéo inversée). Même présentation que le ticket
          // image, mais rendu par l'imprimante en ESC/POS (aucune image → aucune saturation USB).
          push(`[C]<font size='tall' color='bg-black'><b>${ln.t}</b></font>`);
        } else {
          push(`[C]${boldTag(ln.t, ln.b)}`);
        }
        break;
      case 'left':
        push(`[L]${boldTag(ln.t, ln.b)}`);
        break;
      case 'pair':
        // COLONNES NATIVES : aucun remplissage JS, deux balises d'alignement.
        push(`[L]${boldTag(ln.l, ln.b)}[R]${boldTag(ln.r, ln.b)}`);
        break;
      case 'marker':
        if (emitMarkers) push(ln.t);
        break;
    }
  }

  const final = out.map((l, i) => {
    // Les balises de routage sont consommées par `printQueue` : on n'y touche jamais.
    if (l.startsWith('[CAT:')) return l;
    const t = opts.killXPattern ? killXDigit(l) : l;
    // Aucune ligne ne doit valoir ACCIDENTELLEMENT `ARTICLES` (un nom de catégorie, une
    // note client…) : seule l'ancre délibérée a le droit d'armer le pré-traitement natif.
    if (!isAnchor[i] && t.trim().toUpperCase() === 'ARTICLES') return `${t} *`;
    return t;
  });

  while (final.length && final[0].trim() === '') final.shift();
  while (final.length && final[final.length - 1].trim() === '') final.pop();
  return final;
};

/** Assemblage final : `\n` seul, au plus une ligne vide consécutive. */
export const joinTicketLines = (lines: string[]): string =>
  lines.join('\n').replace(/\n{3,}/g, '\n\n');
